/**
 * Browser-side mirror of `@upg/core/legacyPageToBlocks`.
 *
 * The SPA bundle deliberately doesn't depend on `@upg/core` (which would
 * pull zod + the full state machine into the shopper-facing JS). The
 * helper is small, pure, and identical in behaviour — the contract is
 * pinned by the server-side test in `packages/core` PLUS the planPageNode
 * tests in this package.
 *
 * If the mapping table here ever diverges from @upg/core's, the
 * `legacyPageToBlocks` server-side migration will produce different
 * shapes than the runner expects. Keep them in sync.
 */
import type { CheckoutBlockDto, CheckoutPageConfigDto, CheckoutPageSubmitDto } from './api.js';

/**
 * See `@upg/core/flows/legacy-page-to-blocks.ts` for the mapping table.
 *
 * @param page The page config (legacy, new, or mixed).
 * @returns A new `CheckoutPageConfigDto` with `blocks`, `formFieldIds`,
 *   and `submit` populated. Legacy fields are preserved on the output
 *   during the one-release deprecation window.
 */
export function legacyPageToBlocks(page: CheckoutPageConfigDto): CheckoutPageConfigDto {
  let seq = 0;
  const newBlockId = (prefix: string): string => `${prefix}-${++seq}`;

  const hasNewBlocks = Array.isArray(page.blocks) && page.blocks.length > 0;
  const hasNewFormFieldIds = Array.isArray(page.formFieldIds);
  const hasNewSubmit = !!page.submit;

  const layout = page.layout;
  const title = page.title;
  const subtitle = page.subtitle;
  const showFieldIds = page.showFieldIds;
  const submitLabel = page.submitLabel;

  const blocks: CheckoutBlockDto[] = hasNewBlocks
    ? page.blocks!
    : deriveBlocksFromLegacy(layout, title, subtitle, newBlockId);

  let formFieldIds: (string | '*')[];
  if (hasNewFormFieldIds) {
    formFieldIds = page.formFieldIds!;
  } else if (showFieldIds !== undefined) {
    formFieldIds = [...showFieldIds];
  } else if (layout === 'lookup') {
    formFieldIds = ['*'];
  } else {
    formFieldIds = [];
  }

  let submit: CheckoutPageSubmitDto;
  if (hasNewSubmit) {
    submit = page.submit!;
  } else {
    const action: CheckoutPageSubmitDto['action'] = layout === 'result' ? 'cancel' : 'advance';
    submit = submitLabel ? { action, label: submitLabel } : { action };
  }

  return { ...page, blocks, formFieldIds, submit };
}

function deriveBlocksFromLegacy(
  layout: CheckoutPageConfigDto['layout'],
  title: Record<string, string> | undefined,
  subtitle: Record<string, string> | undefined,
  newBlockId: (prefix: string) => string,
): CheckoutBlockDto[] {
  const head: CheckoutBlockDto[] = [];
  if (title && hasAnyLocale(title)) {
    head.push({ id: newBlockId('blk-heading'), type: 'heading', level: 'h2', text: title });
  }
  if (subtitle && hasAnyLocale(subtitle)) {
    head.push({ id: newBlockId('blk-paragraph'), type: 'paragraph', text: subtitle });
  }

  if (!layout) return head;

  switch (layout) {
    case 'lookup':
    case 'custom':
      return [
        ...head,
        { id: newBlockId('blk-lookup'), type: 'lookup-form' },
        { id: newBlockId('blk-methods'), type: 'method-tiles' },
        { id: newBlockId('blk-consents'), type: 'consents' },
        { id: newBlockId('blk-pay'), type: 'pay-button' },
      ];
    case 'confirm':
      return [
        ...head,
        { id: newBlockId('blk-lookup'), type: 'lookup-form' },
        { id: newBlockId('blk-methods'), type: 'method-tiles' },
        { id: newBlockId('blk-consents'), type: 'consents' },
        { id: newBlockId('blk-summary'), type: 'summary' },
        { id: newBlockId('blk-pay'), type: 'pay-button' },
      ];
    case 'result':
      return head;
    default:
      return head;
  }
}

function hasAnyLocale(rec: Record<string, string>): boolean {
  for (const k of Object.keys(rec)) {
    if (rec[k] && rec[k].length > 0) return true;
  }
  return false;
}
